import intersectionWith from 'lodash/intersectionWith';
import isEqual from 'lodash/isEqual';
import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { I18n } from 'common/i18n';
import { ForgeButton, ForgeCard, ForgeCheckbox, ForgeIcon, ForgeTextField } from '@tylertech/forge-react';
import {
  AccordionContainerV2 as AccordionContainer,
  AccordionPaneV2 as AccordionPane,
} from 'common/components/Accordion';
import MetadataProvider from 'common/visualizations/dataProviders/MetadataProvider';
import { ViewColumn } from 'common/types/viewColumn';
import { CustomLabels, Hierarchy, HierarchyColumnConfig, Vif } from 'common/visualizations/vif';

import { MAX_HIERARCHIES } from 'common/authoring_workflow/constants';
import { AuthoringWorkflowState } from 'common/authoring_workflow/reducers/types';
import {
  getCurrentVif,
  getTableColumns,
  getTableHierarchies,
} from 'common/authoring_workflow/selectors/vifAuthoring';
import {
  duplicateTableHierarchy,
  removeTableHierarchy,
  setActiveHierarchyId,
  showGrandTotal,
  showSubTotal,
  showNetTotal,
  updateCustomLabels,
  updateTableHierarchyName,
} from 'common/authoring_workflow/actions';
import TableHierarchyGroupingHeader, { hierarchyTabTitle } from './components/TableHierarchyGroupingHeader';
import NetTotalEditor from './components/NetTotalEditor';

import { FeatureFlags } from '@socrata/platform/feature-flags';
import EventBus from 'common/visualizations/views/agGridReact/helpers/EventBus';
import { isTableAdvancedTotalingEnabled } from 'common/visualizations/helpers/VifSelectors';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';

const scope = 'shared.visualizations.panes.data.fields.table_hierarchies';

type LabelType = keyof CustomLabels;

type ShowTotalActions = typeof showGrandTotal | typeof showSubTotal | typeof showNetTotal;

export interface TableHierarchyGroupingsProps {
  hierarchyIndex: number;
}

export const TableHierarchyGroupings = (props: TableHierarchyGroupingsProps) => {
  const { hierarchyIndex } = props;
  const [allColumnMetadata, setAllColumnMetadata] = useState<ViewColumn[]>([]);
  const dispatch = useDispatch();

  const vif: Vif = useSelector((state: AuthoringWorkflowState) => {
    return getCurrentVif(state.vifAuthoring);
  }, isEqual);

  const tableHierarchies: Hierarchy[] = useSelector(
    (state: AuthoringWorkflowState) => getTableHierarchies(state.vifAuthoring),
    isEqual,
  );

  const hierarchy = tableHierarchies[hierarchyIndex] ?? {};

  const hasAggregation = useMemo(() => {
    return hierarchy.hasAggregation;
  }, [hierarchy.hasAggregation]);

  const existingColumnConfigs = useSelector((state: AuthoringWorkflowState) => {
    const tableColumns = getTableColumns(state.vifAuthoring);
    return intersectionWith(
      tableHierarchies[hierarchyIndex]?.columnConfigurations ?? [],
      tableColumns,
      (h: HierarchyColumnConfig, c: ViewColumn) => c.fieldName === h.columnName,
    );
  }, isEqual);

  const hierarchyGroupingColumnConfigs = existingColumnConfigs.filter(
    (columnConfig) => columnConfig.isGrouping,
  );

  useEffect(() => {
    async function updateColumnMetadata() {
      const metadata = await MetadataProvider.fromVif(vif).getDatasetMetadata();
      setAllColumnMetadata(metadata.columns || []);
    }
    updateColumnMetadata();
  }, [vif]);

  const columnFormats = useMemo(() => get(vif, 'series[0].formatting.columnFormat', {}), [vif]);
  const isGrandTotalsCheckboxEnabled = useMemo(() => {
    if (!isTableAdvancedTotalingEnabled()) {
      return true; // Checkbox is always enabled for the old UI
    }
    return hierarchy.hasAggregation;
  }, [hierarchy.hasAggregation]);

  const isSubTotalsCheckboxEnabled = useMemo(() => {
    if (!isTableAdvancedTotalingEnabled()) {
      return true; // Checkbox is always enabled for the old UI
    }

    return hierarchy.rowGroupCount && hierarchy.rowGroupCount > 0 && hasAggregation;
  }, [hierarchy.rowGroupCount, hasAggregation]);

  const isAddCustomFormulaEnabled = useMemo(() => {
    return hierarchy.rowGroupCount && hierarchy.rowGroupCount > 0;
  }, [hierarchy.rowGroupCount]);

  const accordionPaneTitle = (
    <TableHierarchyGroupingHeader
      columnMetadata={allColumnMetadata}
      hierarchy={hierarchy}
      duplicationAllowed={tableHierarchies.length < MAX_HIERARCHIES}
      removeAllowed={tableHierarchies.length > 1}
      hierarchyGroupingColumnConfigs={hierarchyGroupingColumnConfigs}
      setActiveHierarchy={() => dispatch(setActiveHierarchyId(hierarchy.id))}
      handleOnDuplicateHierarchy={() => dispatch(duplicateTableHierarchy(hierarchyIndex))}
      handleOnRemoveHierarchy={() => dispatch(removeTableHierarchy(hierarchyIndex))}
      handleOnUpdateHierarchyName={(name) => {
        dispatch(updateTableHierarchyName(hierarchyIndex, name));
      }}
      columnFormats={columnFormats}
    />
  );

  const handleCheckboxClick = useCallback(
    (showTotalAction: ShowTotalActions, controlBoolean: boolean, labelType?: LabelType) => {
      dispatch(showTotalAction(hierarchyIndex, !controlBoolean));

      // Reset the label to empty string upon unchecking when advance totaling is enabled
      if (controlBoolean && labelType && isTableAdvancedTotalingEnabled()) {
        const currentLabels = hierarchy.customLabels || {};
        const newLabels = {
          ...currentLabels,
          [labelType]: '',
        };
        dispatch(updateCustomLabels(hierarchyIndex, newLabels));
      }
    },
    [dispatch, hierarchyIndex, hierarchy.customLabels],
  );

  const handleLabelChange = useCallback(
    (labelType: LabelType, value: string) => {
      const currentLabels = hierarchy.customLabels || {};
      const newLabels = {
        ...currentLabels,
        [labelType]: value,
      };
      dispatch(updateCustomLabels(hierarchyIndex, newLabels));
    },
    [dispatch, hierarchyIndex, hierarchy.customLabels],
  );

  const abstractTotalLabelField = (cssLabel: string, controlBoolean: boolean, labelType?: LabelType) => {
    const inputId = `${cssLabel}-${hierarchyIndex}-label-input`;
    const inputTestId = `table-${cssLabel}-label-input`;
    const currentLabel = labelType ? hierarchy.customLabels?.[labelType] || '' : '';
    const placeholderText = labelType ? I18n.t(`${labelType}_label_placeholder`, { scope }) : '';
    const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (labelType) {
        handleLabelChange(labelType, event.target.value);
      }
    };

    return (
      <>
        {controlBoolean && labelType && (
          <div className={`${cssLabel}-label-input abstract-totals-input-field`}>
            <ForgeTextField density={'small'}>
              <input
                type="text"
                id={inputId}
                value={currentLabel}
                onChange={onInputChange}
                placeholder={placeholderText}
                data-testid={inputTestId}
              />
            </ForgeTextField>
          </div>
        )}
      </>
    );
  };
  const renderAbstractTotal = (
    cssLabel: string,
    controlBoolean: boolean,
    showTotalAction: ShowTotalActions,
    translationKey: string,
    labelType?: LabelType,
    disabled?: boolean,
  ) => {
    const checkboxId = `show-${cssLabel}-${hierarchyIndex}-checkbox`;
    const testId = `table-show-${cssLabel}-checkbox`;

    const onCheckboxClick = () => handleCheckboxClick(showTotalAction, controlBoolean, labelType);
    const checked = isTableAdvancedTotalingEnabled() ? controlBoolean && hasAggregation : controlBoolean;
    if (disabled && controlBoolean) {
      dispatch(showTotalAction(hierarchyIndex, false));
    }
    return (
      <div className={`${cssLabel}s-container abstract-totals-container`}>
        <ForgeCheckbox
          id={checkboxId}
          checked={checked}
          onClick={onCheckboxClick}
          data-testid={testId}
          disabled={disabled}
        >
          {I18n.t(translationKey, { scope })}
        </ForgeCheckbox>
        {isTableAdvancedTotalingEnabled() && abstractTotalLabelField(cssLabel, controlBoolean, labelType)}
      </div>
    );
  };

  const renderGrandTotals = () =>
    renderAbstractTotal(
      'grand-total',
      hierarchy.showGrandTotal,
      showGrandTotal,
      'show_totals',
      'totals',
      !isGrandTotalsCheckboxEnabled,
    );

  const renderSubTotals = () =>
    renderAbstractTotal(
      'subtotal',
      hierarchy.showSubTotal,
      showSubTotal,
      'show_subtotals',
      'subtotals',
      !isSubTotalsCheckboxEnabled,
    );

  const renderNetTotals = () =>
    renderAbstractTotal('net-total', hierarchy.showNetTotal, showNetTotal, 'show_net_totals');

  const tabTitle = useMemo(
    () => hierarchyTabTitle(hierarchy, hierarchyGroupingColumnConfigs, allColumnMetadata, columnFormats),
    [hierarchy, hierarchyGroupingColumnConfigs, allColumnMetadata, columnFormats],
  );

  const renderCustomFormulaButton = () => {
    return (
      <ForgeButton
        className={'custom-formula-button'}
        data-testid="custom-formula-button"
        onClick={() => {
          EventBus.dispatch('advancedTotaling', {
            advancedTotalingModalTitle: tabTitle,
            isTableAdvancedTotalingModalOpen: true,
            advancedTotalingModalHierarchyId: hierarchy.id,
          });
        }}
        disabled={!isAddCustomFormulaEnabled}
      >
        <ForgeIcon name={'add'} />
        <span>{I18n.t('shared.components.advanced_totaling.add_custom_formula')}</span>
      </ForgeButton>
    );
  };

  // INFO: EN-85363 To be deprecated / removed once enable_table_advanced_totaling is set to true permanently
  const renderLegacyTableHierarchyGroupings = () => (
    <AccordionContainer>
      <AccordionPane
        title={accordionPaneTitle}
        toggleOnCaretOnly
        onOpen={() => {
          dispatch(setActiveHierarchyId(hierarchy.id));
        }}
      >
        <>
          <div className="table-hierarchies-list" data-testid="table-hierarchies-list">
            <div className="totals-container">
              {renderGrandTotals()}
              {renderSubTotals()}
              {FeatureFlags.value('enable_prototype_net_total') && renderNetTotals()}
            </div>
            {FeatureFlags.value('enable_prototype_net_total') && hierarchy.showNetTotal && (
              <NetTotalEditor hierarchyIndex={hierarchyIndex} />
            )}
          </div>
        </>
      </AccordionPane>
    </AccordionContainer>
  );

  return isTableAdvancedTotalingEnabled() ? (
    <ForgeCard className="table-hierarchy">
      <div className="table-hierarchy--header__container">{accordionPaneTitle}</div>
      <div>
        {renderGrandTotals()}
        {renderSubTotals()}
        {FeatureFlags.value('enable_prototype_net_total') && renderNetTotals()}
      </div>
      {FeatureFlags.value('enable_prototype_net_total') && hierarchy.showNetTotal && (
        <NetTotalEditor hierarchyIndex={hierarchyIndex} />
      )}
      {(hierarchy.showSubTotal || hierarchy.showGrandTotal) && renderCustomFormulaButton()}
    </ForgeCard>
  ) : (
    renderLegacyTableHierarchyGroupings()
  );
};
